/**
 * TrackItemScopeSection
 *
 * Lets a track owner configure which (category × altitude) cells this track
 * covers.  Rendered on the Defaults tab of TrackDetail.
 *
 * Rules:
 *  - Empty selection  → UNSCOPED (track accepts any item)
 *  - Single cell      → sets required_item_category + required_altitude_level
 *  - Multiple cells   → UNSCOPED on the primary columns; full list stored in
 *                       tracks.metadata.trackCells for display and filtering
 */

import { useCallback, useMemo, useState } from 'react';
import { GridCellSelector } from '@/components/prioritization/sessions/GridCellSelector';
import { useTrackStore } from '@/stores/prioritization/trackStore';
import { useItemsStore } from '@/stores/prioritization/itemsStore';
import {
  UNSCOPED_CATEGORY,
  UNSCOPED_ALTITUDE,
  TRACK_CELLS_METADATA_KEY,
  getTrackCellsFromMetadata,
  type Track,
} from '@/types/prioritization/track';
import type { GridCellFilter } from '@/types/prioritization/session';
import type { AltitudeLevel } from '@/types/prioritization/itemLifecycle';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function trackToSelectedCells(track: Track): GridCellFilter[] {
  // Multi-cell tracks store the full list in metadata
  const cells = getTrackCellsFromMetadata(track.metadata);
  if (cells && cells.length > 0) {
    return cells.map(c => ({
      itemCategory: c.itemCategory as GridCellFilter['itemCategory'],
      altitudeLevel: c.altitudeLevel as AltitudeLevel,
    }));
  }
  // Single-cell track
  if (
    track.requiredItemCategory !== UNSCOPED_CATEGORY &&
    track.requiredAltitudeLevel !== UNSCOPED_ALTITUDE
  ) {
    return [
      {
        itemCategory: track.requiredItemCategory as GridCellFilter['itemCategory'],
        altitudeLevel: track.requiredAltitudeLevel as AltitudeLevel,
      },
    ];
  }
  return [];
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface TrackItemScopeSectionProps {
  track: Track;
  /** Called after a successful scope save with the committed cell list. */
  onAfterSave?: (newCells: GridCellFilter[]) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function TrackItemScopeSection({ track, onAfterSave }: TrackItemScopeSectionProps) {
  const updateTrack = useTrackStore((s) => s.updateTrack);
  const allItems = useItemsStore((s) => s.items);
  const [saving, setSaving] = useState(false);

  // Count workspace items per (altitudeLevel × itemCategory) cell so the grid
  // shows how many items live in each cell — key format: `${altitudeLevel}-${itemCategory}`
  const cellCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const item of allItems) {
      if (!item.altitudeLevel || !item.itemCategory) continue;
      const key = `${item.altitudeLevel}-${item.itemCategory}`;
      counts[key] = (counts[key] ?? 0) + 1;
    }
    return counts;
  }, [allItems]);

  const initialCells = useMemo(() => trackToSelectedCells(track), [track]);
  const [selectedCells, setSelectedCells] = useState<GridCellFilter[]>(initialCells);

  // Reset local state when the track prop changes (e.g. after a successful save)
  const trackId = track.id;
  const [lastTrackId, setLastTrackId] = useState(trackId);
  if (trackId !== lastTrackId) {
    setLastTrackId(trackId);
    setSelectedCells(trackToSelectedCells(track));
  }

  const isDirty = useMemo(() => {
    const current = trackToSelectedCells(track);
    if (current.length !== selectedCells.length) return true;
    const currentKeys = new Set(current.map(c => `${c.altitudeLevel}-${c.itemCategory}`));
    return selectedCells.some(c => !currentKeys.has(`${c.altitudeLevel}-${c.itemCategory}`));
  }, [track, selectedCells]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const primaryCategory =
        selectedCells.length === 1 ? selectedCells[0].itemCategory : UNSCOPED_CATEGORY;
      const primaryAltitude =
        selectedCells.length === 1 ? selectedCells[0].altitudeLevel : UNSCOPED_ALTITUDE;

      const nextMetadata: Record<string, unknown> = { ...(track.metadata ?? {}) };
      if (selectedCells.length > 1) {
        nextMetadata[TRACK_CELLS_METADATA_KEY] = selectedCells.map(c => ({
          itemCategory: c.itemCategory,
          altitudeLevel: c.altitudeLevel,
        }));
      } else {
        delete nextMetadata[TRACK_CELLS_METADATA_KEY];
      }

      await updateTrack(track.id, {
        requiredItemCategory: primaryCategory,
        requiredAltitudeLevel: primaryAltitude,
        metadata: Object.keys(nextMetadata).length > 0 ? nextMetadata : null,
      });
      onAfterSave?.(selectedCells);
    } finally {
      setSaving(false);
    }
  }, [track.id, track.metadata, selectedCells, updateTrack, onAfterSave]);

  const handleDiscard = useCallback(() => {
    setSelectedCells(trackToSelectedCells(track));
  }, [track]);

  return (
    <div className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface-raised)] overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 flex items-center justify-between border-b border-[var(--rule)] dark:border-[var(--rule)]">
        <div>
          <h3 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Item scope</h3>
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] mt-0.5">
            Which item types this track covers.{' '}
            {selectedCells.length === 0
              ? 'No filter — accepts any item.'
              : selectedCells.length === 1
              ? `Restricted to ${selectedCells[0].itemCategory} · Level ${selectedCells[0].altitudeLevel}.`
              : `${selectedCells.length} cells combined.`}
          </p>
        </div>
        {isDirty && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleDiscard}
              disabled={saving}
              className="text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:text-[var(--text-sec)] dark:hover:text-[var(--text-pri)] disabled:opacity-50"
            >
              Discard
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving}
              className="text-xs font-medium px-2.5 py-1 rounded bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        )}
      </div>

      {/* Grid */}
      <div className="p-4">
        <GridCellSelector
          value={selectedCells}
          onChange={setSelectedCells}
          allowMultiple
          cellCounts={cellCounts}
          label="Grid cells"
          description="No cell selected — Track accepts any item type"
        />
      </div>
    </div>
  );
}

export default TrackItemScopeSection;
